import { useEffect, useRef } from 'react';
import { SdbEnhancedRate } from '~/constants/ff.posthog';
import type { IAverageRateEntry } from '~/domain/Jobs/types/averageRateTypes';
import { useFeatureFlagVariant } from '~/hooks/useFeatureFlagVariant';
import { usePosthog } from '~/hooks/usePosthog';
import { fireShiftRequestEnhancedRateExperimentExposed } from '~/posthogs/events';
import { isSingleShiftWithin48h } from '../utils/singleShiftTiming';

interface IUseEnhancedRecommendedRateParams {
  /** The selected profession's recommendation entry (may carry `enhancedRate`). */
  recommendation?: IAverageRateEntry;
  /** The standard recommended (rounded average) rate. Returned as-is when not enhanced. */
  averageRate: number;
  /** Selected shift dates; a single date is required to qualify. */
  dates?: Date[];
  /** Shift arrival time (`"h:mm am/pm"`), combined with the date to check the 48h window. */
  arrivalTime?: string;
}

interface IUseEnhancedRecommendedRateResult {
  /** Rate to default/prefill/display as the recommendation — enhanced when eligible, else standard. */
  recommendedRate: number;
  /** Whether the enhanced rate is actually being applied (test variant + eligible + BE value present). */
  isEnhanced: boolean;
}

/**
 * Resolves the recommended rate shown on the Shift Details step for the SDB enhanced-rate
 * experiment.
 *
 * For a single shift within 48h of start, the `test` variant defaults to the backend-provided
 * `enhancedRate`; everyone else (control, multi-shift, >48h, or no backend value) keeps the
 * standard `average` rate. The standard `averageRate` is intentionally left untouched so callers
 * keep referencing it for the "likely/unlikely to fill" indicator.
 *
 * Exposure is fired once per qualifying request regardless of variant, so the control group is
 * measured against the same audience that could have seen the enhanced rate.
 */
export const useEnhancedRecommendedRate = ({
  recommendation,
  averageRate,
  dates,
  arrivalTime,
}: IUseEnhancedRecommendedRateParams): IUseEnhancedRecommendedRateResult => {
  const posthog = usePosthog();
  const isTestVariant = useFeatureFlagVariant(SdbEnhancedRate, 'test');

  const enhancedRate = recommendation?.enhancedRate;
  // A request only qualifies when it is a single shift within 48h AND the backend actually
  // returned an enhanced rate for the profession.
  const isEligible = enhancedRate != null && isSingleShiftWithin48h(dates, arrivalTime);
  const isEnhanced = isTestVariant && isEligible;
  const recommendedRate = isEnhanced ? Math.round(enhancedRate) : averageRate;

  const exposedRef = useRef(false);
  useEffect(() => {
    if (exposedRef.current || !isEligible) return;
    exposedRef.current = true;

    const variant = posthog?.getFeatureFlagVariant(SdbEnhancedRate);
    fireShiftRequestEnhancedRateExperimentExposed({
      flagKey: SdbEnhancedRate,
      variant: typeof variant === 'string' ? variant : String(variant),
    });
  }, [isEligible, posthog]);

  return { recommendedRate, isEnhanced };
};
